import { useEffect } from "react";
import type {
	EngagementDockContentState,
	EngagementProvider,
	EngagementProviderEvent,
	EngagementModalKind,
} from "@/provider/EngagementProvider";
import type { ActiveDockTarget } from "@/orchestrators/useCaptainUpIntegration";
import type { ShellRuntimeAction } from "@/shell/ShellRuntimeReducer";
import { resolveDockContentUpdate } from "@/shell/ShellDockPolicy";
import { debugLog } from "@/utils/debug";

export type UseShellProviderEventsInput = {
	provider: EngagementProvider | null;
	dispatch: React.Dispatch<ShellRuntimeAction>;
	activeDockTargetRef: React.MutableRefObject<ActiveDockTarget>;
	popupGrantedRef: React.MutableRefObject<boolean>;
};

function isAuthoritativeModalClosedEvent(
	eventKind: EngagementModalKind,
	activeDockTarget: ActiveDockTarget,
	popupGranted: boolean
): boolean {
	if (!popupGranted) {
		return false;
	}

	return eventKind === activeDockTarget;
}

export function useShellProviderEvents({
	provider,
	dispatch,
	activeDockTargetRef,
	popupGrantedRef,
}: UseShellProviderEventsInput): void {
	useEffect(() => {
		if (!provider) {
			debugLog(
				"[EF ShellProviderEvents] provider event subscription skipped because provider is missing"
			);
			return;
		}

		const unsubscribe = provider.subscribe(
			(event: EngagementProviderEvent) => {
				const activeDockTarget = activeDockTargetRef.current;
				const popupGranted = popupGrantedRef.current;

				debugLog("[EF ShellProviderEvents] provider event", {
					event,
					popupGranted,
					activeDockTarget,
				});

				switch (event.type) {
					case "pause_resume": {
						if (event.value === "pause") {
							debugLog(
								"[EF ShellProviderEvents] provider pause accepted as authoritative popup-open intent",
								{
									activeDockTarget,
									popupGranted,
								}
							);

							dispatch({
								type: "HANDLE_PROVIDER_PAUSE",
							});
							return;
						}

						debugLog(
							"[EF ShellProviderEvents] provider resume accepted as authoritative popup-close intent",
							{
								activeDockTarget,
								popupGranted,
							}
						);

						dispatch({
							type: "HANDLE_PROVIDER_RESUME",
							payload: {
								nextPhase: popupGranted ? "closing" : "closed",
							},
						});
						return;
					}

					case "modal_closed": {
						const authoritative = isAuthoritativeModalClosedEvent(
							event.kind,
							activeDockTarget,
							popupGranted
						);

						if (!authoritative) {
							debugLog(
								"[EF ShellProviderEvents] modal_closed ignored because it does not match the active granted popup",
								{
									eventKind: event.kind,
									eventSource: event.source,
									activeDockTarget,
									popupGranted,
								}
							);
							return;
						}

						debugLog(
							"[EF ShellProviderEvents] modal_closed accepted as authoritative popup close",
							{
								eventKind: event.kind,
								eventSource: event.source,
								activeDockTarget,
								popupGranted,
							}
						);

						dispatch({
							type: "HANDLE_PROVIDER_MODAL_CLOSED",
							payload: {
								nextPhase: popupGranted ? "closing" : "closed",
							},
						});
						return;
					}

					case "provider_reset_required": {
						dispatch({
							type: "HANDLE_PROVIDER_RESET_REQUIRED",
							payload: {
								nextPhase: popupGranted ? "closing" : "closed",
								reason: event.reason,
							},
						});
						return;
					}

					case "modal_content_changed": {
						debugLog(
							"[EF ShellProviderEvents] modal_content_changed stored as passive content state only",
							{
								hasContent: event.hasContent,
								activeDockTarget,
								popupGranted,
							}
						);

						dispatch({
							type: "SET_MODAL_HAS_CONTENT",
							payload: event.hasContent,
						});
						return;
					}

					case "dock_content_changed": {
						const nextContentState: EngagementDockContentState = {
							missionHasContent: event.missionHasContent,
							tournamentHasContent: event.tournamentHasContent,
						};

						const resolution = resolveDockContentUpdate(
							activeDockTarget,
							nextContentState
						);

						debugLog(
							"[EF ShellProviderEvents] resolved dock content update in shell policy",
							{
								currentDockTarget: activeDockTarget,
								nextDockTarget: resolution.nextActiveDockTarget,
								shouldSwitchTarget: resolution.shouldSwitchTarget,
								missionHasContent: nextContentState.missionHasContent,
								tournamentHasContent:
									nextContentState.tournamentHasContent,
							}
						);

						dispatch({
							type: "APPLY_DOCK_CONTENT_STATE",
							payload: {
								contentState: resolution.nextDockContentState,
								nextActiveDockTarget:
									resolution.nextActiveDockTarget,
							},
						});

						return;
					}

					case "vendor_event":
					case "reward_acquired": {
						return;
					}
				}
			}
		);

		return () => {
			unsubscribe();
		};
	}, [activeDockTargetRef, dispatch, popupGrantedRef, provider]);
}

export default useShellProviderEvents;